import { BackAppBar, MessageInput, MessageItem } from "@/components/ui";
import useMessageMutation from "@/hooks/useMessageMutation";
import useProfile from "@/hooks/useProfile";
import { QUERY_KEYS } from "@/libraries/constant";
import { fromUnixToDateString } from "@/libraries/date";
import { capitalized, getMessageTagLabel, randUuid } from "@/libraries/helper";
import {
	getGroupQueryOptions,
	getMessageQueryOptions,
	markReadAllMessageQueryOptions,
} from "@/queries";
import {
	DataIdSchema,
	MessageDetailSearchParams,
	PostCreateMessageBodySchema,
} from "@/schemas";
import type { MessageDiscussion } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type QueryClient,
	onlineManager,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { cloneDeep } from "lodash";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import isString from "lodash/isString";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLongPress } from "use-long-press";
import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import ContextMenuMessage from "../-components/ContextMenuMessage";

export const Route = createFileRoute("/dashboard/chat/message/$id")({
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	validateSearch: zodSearchValidator(MessageDetailSearchParams),
	beforeLoad: async ({ context, params, search, cause }) => {
		const queryClient: QueryClient = context.queryClient;
		if (cause === "enter" && params.id && search?.cid) {
			const message = await queryClient.fetchQuery(
				getMessageQueryOptions(search.cid),
			);
			if (
				message.message.unread_thread_messages &&
				message?.message?.channel_name &&
				params.id
			)
				await queryClient.prefetchQuery(
					markReadAllMessageQueryOptions({
						channel_name: message?.message?.channel_name,
						message_id: params.id,
					}),
				);
		}
	},
	loader: ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureQueryData(
				getMessageQueryOptions(params.id),
			);
		}
		return context.queryClient.getQueryData([
			QUERY_KEYS.GET_MESSAGE,
			params.id,
		]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigation = Route.useNavigate();
	const senderName = Route.useSearch({
		select: (s) => s.sender,
	});
	const channelId = Route.useSearch({
		select: (s) => s.cid as number,
	});
	const messageId = Route.useParams({
		select: (s) => s.id,
	});
	const fileIdx = Route.useSearch({
		select: (s) => s["file-idx"],
	});
	const lightBoxes = useRouterState({
		// @ts-expect-error
		select: (s) => (s.location.state.lightBoxes || []) as Array<PhotoProps>,
	});
	const { profile } = useProfile();

	const scrollRef = useRef<HTMLDivElement>(null);

	const [cursorPosition, setCursorPosition] = useState<Record<
		"x" | "y",
		number
	> | null>(null);
	const [contextMessage, setContextMessage] =
		useState<MessageDiscussion | null>(null);

	const messageQuery = useSuspenseQuery(getMessageQueryOptions(messageId));

	const form = useForm<PostCreateMessageBodySchema>({
		mode: "all",
		resolver: zodResolver(PostCreateMessageBodySchema),
		defaultValues: async () => ({
			channel_name: messageQuery.data.message.channel_name,
			messageable_id: messageQuery.data.message.id_db.toString(),
			messageable_type: "Message",
		}),
	});

	const { readMessage } = useMessageMutation();

	const channelName = useMemo(
		() => messageQuery.data.message.channel_name,
		[messageQuery],
	);
	const message = useMemo(() => {
		if (!messageQuery.data.message) return messageQuery.data.message;
		const currentMessage = messageQuery.data.message.messages.find(
			(m) => m.id === messageQuery.data.message.id,
		);
		if (!currentMessage) return messageQuery.data.message;
		return {
			...messageQuery.data.message,
			...(currentMessage ?? {}),
		};
	}, [messageQuery]);

	const channelType = useMemo(() => {
		if (message.channel_name.includes("single")) {
			return "direct";
		}
		return "group";
	}, [message]);

	const queryGroup = useQuery(
		getGroupQueryOptions(channelId, {
			enabled: !!channelId && channelType === "group",
		}),
	);

	const memberIds = useMemo(() => {
		if (channelType === "direct") {
			return [channelId];
		}
		if (channelType === "group" && queryGroup.data?.group) {
			return queryGroup.data.group.members.map((m) => m.id);
		}
		return [];
	}, [channelType, channelId, queryGroup.data]);

	const messages = useMemo(() => {
		let messageItems = message.messages?.length
			? cloneDeep(message.messages)
			: [];
		if (!messageItems.length) return messageItems;
		messageItems = messageItems
			.filter((m) => m.id !== message.id)
			.map((messageItem) => ({
				...messageItem,
				_isMe: messageItem.user?.id === profile?.id,
				_flag:
					messageItem.id_db === -1
						? ("pending" as const)
						: messageItem.id_db > 0 &&
								(!messageItem.reads.length || // When no one read the message or
									(messageItem.reads.length === 1 && // only the sender read the message
										messageItem.reads[0] === profile?.id)) // and the sender is the current user
							? ("sent" as const)
							: memberIds.some((id) => !messageItem.reads.includes(id)) // When not all members read the message
								? ("delivered" as const)
								: memberIds.every((id) => messageItem.reads.includes(id)) // When all members read the message
									? ("read" as const)
									: ("pending" as const),
			}));
		const orderedMessages = orderBy(messageItems, "updated_at", "asc");
		const groupedMessages = groupBy(orderedMessages, (m) =>
			fromUnixToDateString(m.updated_at),
		);
		const flatMessages = flatMap(
			groupedMessages,
			(messages, date) =>
				[
					{
						id: randUuid(10),
						text: date,
						system_message: true,
						tags: "date",
						channel_name: channelName ?? "",
						id_db: 0,
						created_at: messages[0].created_at,
						updated_at: messages[0].updated_at,
						document_count: 0,
						document_size: "0.0 Kb",
						sent: false,
						delivered: false,
						strike: false,
						is_deleted: false,
						reply_to: null,
						invitation_status: null,
						latest_reply_date: null,
						reply_count: 0,
						reads: [],
						mention_name: [],
						user: null,
						parent: null,
						replies: [],
						documents: [],
						mentions: [],
						preview: null,
					},
					...messages,
				] as MessageDiscussion[],
		);
		return uniqBy(flatMessages, "id");
	}, [message, channelName, profile?.id, memberIds]);

	const messageTag = useMemo(() => {
		const tag = getMessageTagLabel(message, "chats");
		if (!tag?.length) return "";
		if (isString(tag)) {
			if (tag.toLowerCase() === "media") return "";
			return capitalized(tag);
		}
		if (Array.isArray(tag) && tag.length) return capitalized(tag[0]);
	}, [message]);

	const prevUnreadIdsRef = useRef<string[]>([]);
	const unreadMessageIds = useMemo(
		() => [
			...new Set(
				message.messages
					.filter(
						(m) =>
							!m.system_message &&
							m.user?.id !== profile?.id &&
							// @ts-expect-error
							!m.reads.includes(profile?.id),
					)
					.map((m) => m.id),
			),
		],
		[message.messages, profile],
	);

	const onOpenContextMenu = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, message: MessageDiscussion) => {
			if (message.is_deleted) return;
			e.preventDefault();
			e.stopPropagation();
			setContextMessage(message);
			return setCursorPosition({ x: e.clientX, y: e.clientY });
		},
		[],
	);

	const onCloseContextMenu = useCallback(() => {
		setCursorPosition(null);
		return setContextMessage(null);
	}, []);

	const onLongPressMessage = useLongPress<HTMLDivElement, MessageDiscussion>(
		(e, meta) =>
			meta.context &&
			onOpenContextMenu(e as React.MouseEvent<HTMLDivElement>, meta.context),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (unreadMessageIds.length && channelName && !readMessage.isPending) {
			readMessage.mutate({
				channel_name: channelName,
				message_ids: unreadMessageIds,
				message_id: message.id_db,
			});
		}
	}, [unreadMessageIds]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const isSameAsPrev =
			prevUnreadIdsRef.current.length === unreadMessageIds.length &&
			prevUnreadIdsRef.current.every((id, idx) => id === unreadMessageIds[idx]);

		if (!isSameAsPrev && unreadMessageIds.length > 0 && message.channel_name) {
			readMessage.mutate({
				channel_name: channelName,
				message_ids: unreadMessageIds,
				message_id: message.id_db,
			});

			prevUnreadIdsRef.current = unreadMessageIds;
		}
	}, [unreadMessageIds, channelName]);

	if (!profile) return;

	return (
		<>
			<BackAppBar
				className="!bg-gradient-chat [&_*_]:text-white"
				title={`${senderName}: ${messageTag}`}
			/>
			<div className="py-3 bg-zinc-200 sticky">
				<MessageItem
					profileId={profile?.id}
					message={message}
					senderName={senderName}
					className="container"
					chatModule="chats"
					enableFetchMessage
					onContextMenu={(e) => onOpenContextMenu(e, message)}
					{...onLongPressMessage(message)}
				/>
			</div>
			<div ref={scrollRef} className="flex-1 overflow-auto">
				{messages.map((message) => (
					<MessageItem
						key={message.id}
						profileId={profile?.id}
						message={message}
						className="container !py-2"
						chatModule="chats"
						onContextMenu={(e) => onOpenContextMenu(e, message)}
						{...onLongPressMessage(message)}
					/>
				))}
			</div>
			<FormProvider {...form}>
				<div className="bg-white container py-2">
					<MessageInput
						channelType={channelType}
						chatModule="chats"
						inputMediaOnly={!message.document_count}
						hideInputTag={!!message.document_count}
						onSuccessSubmit={() =>
							scrollRef.current?.lastElementChild?.scrollIntoView({
								behavior: "smooth",
							})
						}
					/>
				</div>
				{contextMessage ? (
					<ContextMenuMessage
						isReadonly={false}
						channelName={messageQuery.data.message.channel_name}
						moduleType="chats"
						message={contextMessage}
						profileId={profile.id}
						position={cursorPosition}
						onClose={onCloseContextMenu}
					/>
				) : null}
			</FormProvider>
			{lightBoxes.length ? (
				<Lightbox
					open={true}
					index={fileIdx}
					plugins={[Counter, Download, Thumbnails, Video]}
					slides={lightBoxes?.map((lightBox) => ({
						src: lightBox.src,
						title: lightBox.alt,
					}))}
					close={() => {
						return navigation({
							search: {
								cid: channelId,
								sender: senderName,
							},
							state: {
								lightBoxes: undefined,
							},
							replace: true,
						});
					}}
				/>
			) : null}
		</>
	);
}
